import { Sizes } from '@anm/components/PlayerWrapper';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { FC, useCallback } from 'react';

import LeaveTabAlert from '../../../components/hosting/CustomizationSection/LeaveTabAlert';
import Notifications from '../../../components/hosting/CustomizationSection/Notifications';
import SaveBlock from '../../../components/hosting/CustomizationSection/SaveBlock';
import useHandlers from '../../../components/hosting/CustomizationSection/hooks/useHandlers';
import useLeaveAlert from '../../../components/hosting/CustomizationSection/hooks/useLeaveAlert';
import useTogglers from '../../../components/hosting/CustomizationSection/hooks/useTogglers';
import AccessTab from '../AccessTab';
import EmailTab from '../../../components/hosting/CustomizationSection/tabs/EmailTab';
import EmbedTab from '../../../components/hosting/CustomizationSection/tabs/EmbedTab';
import LandingTab from '../../../components/hosting/CustomizationSection/tabs/LandingTab';
import LeftBarLayout, { LeftBarLayoutItem } from '../../../components/layouts/LeftBarLayout';
import { WEBINAR_TABS } from '../../../pages/webinar';
import OpenWebinar from '../OpenWebinar';
import { LiveStream } from 'types/stream';
import downloadWebinarAttendees from '@anm/helpers/downloadWebinarAttendees';
import DomainsTab from '../DomainsTab';
import useIsWebinar from '../../../hooks/useIsWebinar';
import { Features } from '@anm/shared/features';
import { useSelector } from 'react-redux';
import { selectWaveSubscription } from '../../../store/modules/user/selectors';
import usePaidThemeAndLayout from '../../../components/hosting/CustomizationSection/hooks/usePaidThemeAndLayout';

type LayoutProps = {
  title: string;
  sizes: Sizes;
  rooomId: string;
  stream: LiveStream;
  webinarId: string;
  activeButton: WEBINAR_TABS;
  onActiveButtonChange: (index: WEBINAR_TABS) => void;
};

const Layout: FC<LayoutProps> = ({ sizes, title, stream, rooomId, webinarId, activeButton, onActiveButtonChange }) => {
  const [wasChanged, getHandlers] = useHandlers(webinarId);
  const { save, discard, isPending } = getHandlers();
  const canUseCustomDomains = Features.canUseCustomDomains(useSelector(selectWaveSubscription));

  const [openTab, setTargetTab, showLeaveAlert, setPrevAlertState, setShowLeaveAlert] = useLeaveAlert<WEBINAR_TABS>({
    webinarId,
    isPending,
    onActiveButtonChange
  });

  const isWebinar = useIsWebinar();

  const { shouldUpgrade, upgradeDescription } = usePaidThemeAndLayout();

  const [togglers] = useTogglers<WEBINAR_TABS>({
    hostId: webinarId,
    wasChanged,
    activeButton,
    isWebinar: true,
    openTab
  });

  const handleSwitchTab = useCallback(
    (tab: WEBINAR_TABS) => {
      setTargetTab(tab);

      switch (true) {
        case wasChanged && isMobileOrIPad():
          discard();
          openTab(tab);
          return;

        case wasChanged:
          setPrevAlertState(true);
          setShowLeaveAlert(true);
          return;

        default:
          openTab(tab);
      }
    },
    [wasChanged]
  );

  const handleCloseAlert = useCallback(() => {
    setTargetTab(null);
    setShowLeaveAlert(false);
  }, []);

  const handleDiscardChanges = () => {
    discard();
    setShowLeaveAlert(false);
  };

  const { embed, landing } = togglers;

  const canDownloadAttendee = stream.state === 'FINISHED';

  return (
    <>
      <Notifications />
      {showLeaveAlert && (
        <LeaveTabAlert {...{ isPending }} onClose={handleCloseAlert} onConfirm={save} onCancel={handleDiscardChanges} />
      )}
      {!isMobileOrIPad() && <SaveBlock {...{ wasChanged, getHandlers, shouldUpgrade, upgradeDescription }} />}
      <LeftBarLayout
        {...{ activeButton, onActiveButtonChange }}
        navButtonVariant="white"
        variant={activeButton === WEBINAR_TABS.domains ? 'default' : 'split'}
        renderNavTopContent={() => (!isMobileOrIPad() ? <OpenWebinar {...{ rooomId }} /> : null)}
        list={
          [
            {
              button: {
                title: 'Webinar page',
                icon: 'images/icons/landing_grey_icon.svg',
                ...(!isWebinar && {
                  switch: {
                    ...landing,
                    onChange: landing.handler
                  }
                }),
                onClick: () => handleSwitchTab(WEBINAR_TABS.webinar)
              },
              renderContent: () => <LandingTab hostId={webinarId} />
            },
            {
              button: {
                title: 'Embed',
                icon: 'images/embed_icon.svg',
                switch: {
                  ...embed,
                  onChange: embed.handler
                },
                onClick: () => handleSwitchTab(WEBINAR_TABS.embed)
              },
              renderContent: () => <EmbedTab hostId={webinarId} />
            },
            {
              button: {
                title: 'Access',
                icon: 'images/password_icon.svg',
                onClick: () => handleSwitchTab(WEBINAR_TABS.access)
              },
              renderContent: () => <AccessTab />
            },
            {
              button: {
                title: 'Email campaign',
                icon: 'images/icons/email_icon.svg',
                onClick: () => handleSwitchTab(WEBINAR_TABS.email)
              },
              renderContent: () => <EmailTab videoName={title} hostId={webinarId} videoSizes={sizes} />
            },
            canUseCustomDomains && {
              button: {
                title: 'Custom domain',
                icon: 'images/icons/ic_custom_domain.svg',
                onClick: () => handleSwitchTab(WEBINAR_TABS.domains)
              },
              renderContent: () => <DomainsTab hostId={webinarId} isWebinar={true} />
            },
            canDownloadAttendee && {
              button: {
                title: 'Download attendees',
                icon: 'images/icons/download_icon_grey_V1.svg',
                onClick: () => downloadWebinarAttendees(stream.streamId)
              }
            }
          ].filter(Boolean) as LeftBarLayoutItem[]
        }
      />
    </>
  );
};

export default Layout;
